/* eslint-disable react/boolean-prop-naming */
import React, { useEffect, useState } from 'react'

import { cn } from '@harvey-frontend/utils/utils'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@harvey-frontend/components/ui/button'
import { Skeleton } from '@harvey-frontend/components/ui/skeleton'

import { MatchedReference, PlaybookRule } from '../../../../schemas/playbook'
import useAppStore from '../../stores/store'
import { SuggestionCard } from './SuggestionCard'

interface ReferenceItemProps {
  reference: MatchedReference
  rule: PlaybookRule
  isHighlighted: boolean
  suggestionsPending: boolean
}

export const ReferenceItem = ({
  reference,
  rule,
  isHighlighted,
  suggestionsPending,
}: ReferenceItemProps) => {
  const [rangeText, setRangeText] = useState(reference.range?.text)

  const { highlightedText, markReferenceHighlighted } = useAppStore(
    useShallow((s) => ({
      highlightedText: s.highlightedText,
      markReferenceHighlighted: s.markReferenceHighlighted,
    }))
  )

  // We need this to sync the range text when suggestions are applied.
  // This is because changes to the Word object range won't trigger a re-render
  // of the component, nor will the text automatically update even when the range
  // is updated.
  useEffect(() => {
    const refreshRangeText = async () => {
      if (reference.range) {
        reference.range.load('text')
        await reference.range.context.sync()
        setRangeText(reference.range.text)
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    refreshRangeText()
  }, [reference.range, highlightedText?.text])

  const handleJumpToText = async () => {
    if (reference.range) {
      reference.range.load('text')
      reference.range.select()
      await reference.range.context.sync()
      markReferenceHighlighted(rule.name, reference.id)
    }
  }
  return (
    <div
      className={cn('flex flex-col rounded-md border', {
        'border-input-focused': isHighlighted,
      })}
    >
      <div
        className={cn(
          'flex items-center gap-2 rounded-t-md border-b bg-secondary-hover p-3',
          {
            'rounded-b-md':
              reference.suggestions.length === 0 && !suggestionsPending,
          }
        )}
      >
        <p className="line-clamp-3 text-xs">{rangeText}</p>
        <div className="flex shrink-0 flex-col justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 transition hover:bg-none"
            onClick={handleJumpToText}
            disabled={isHighlighted}
          >
            Jump to text
          </Button>
          <span className="px-2 text-right text-xs text-muted">
            p. {reference.pageNo}
          </span>
        </div>
      </div>
      {(reference.suggestions.length > 0 || suggestionsPending) && (
        <div className="flex flex-col gap-1.5 p-2">
          {suggestionsPending && <Skeleton rows={1} rowHeight="h-4" />}
          {reference.suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.suggestion.id}
              ruleName={rule.name}
              referenceId={reference.id}
              suggestion={suggestion}
              title={suggestion.type}
              range={reference.range}
              originalText={reference.originalText}
              isApplied={
                reference.appliedSuggestionId === suggestion.suggestion.id
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
